// Fenny i korthet — synlig faktasida och påståendelista.
// Allt innehåll hämtas från sanningsfilerna (company-facts.ts, pricing-facts.ts)
// så sidan aldrig kan säga något annat än AI-exporterna och schemat.
// Varje faktum har en stabil ankarlänk (#id) som AI-tjänster och journalister
// kan länka direkt till. Ankarna kontrolleras av scripts/check-fact-page.mjs.

import { createFileRoute, Link } from "@tanstack/react-router";
import { COMPANY_FACTS } from "@/content/company-facts";
import {
  DELIVERY_PROMISE,
  NATIONAL_PRICING_NOTE,
  PRICE_DATA_BASIS,
  FILM_PRICING,
} from "@/content/pricing-facts";
import { aeoGeo, breadcrumbs } from "@/lib/seo/schema";

const URL = "https://fenny.se/fenny-i-korthet";
const TITLE = "Fenny i korthet — fakta, orter, leverans och org.nr";
const DESC =
  `Fakta om Fenny i fristående stycken med källa och granskningsdatum: vad vi gör, Pulse och Prime, prisunderlag, leverans på ${DELIVERY_PROMISE}, orter och organisationsnummer.`;

const REVIEWED = PRICE_DATA_BASIS.reviewed;
const OWNER = COMPANY_FACTS.editorInChief;

type Fact = { id: string; label: string; text: string; source: string; sourcePath?: string };

const F = COMPANY_FACTS;
const list = (xs: readonly string[]) =>
  xs.length > 1 ? `${xs.slice(0, -1).join(", ")} och ${xs[xs.length - 1]}` : xs[0];

export const FACTS: Fact[] = [
  {
    id: "vad-ar-fenny",
    label: "Vad Fenny är",
    text: `${F.brand} är en svensk filmbyrå som producerar film för företag och organisationer i hela Sverige, antingen som filmabonnemang (Pulse) eller som skräddarsydd projektproduktion (Prime).`,
    source: "Fennys företagsfakta",
    sourcePath: "/om-fenny",
  },
  {
    id: "pulse",
    label: "Pulse",
    text: `Fenny Pulse är filmproduktion på abonnemang till fast månadspris från ${FILM_PRICING.pulseFrom} exkl. moms, utan bindningstid.`,
    source: "Prislistan",
    sourcePath: "/pulse",
  },
  {
    id: "prime",
    label: "Prime",
    text: "Fenny Prime är enskilda skräddarsydda produktioner, till exempel reklamfilm, företagsfilm och employer branding, med fast pris efter brief.",
    source: "Prislistan",
    sourcePath: "/prime",
  },
  {
    id: "pris",
    label: "Prisförutsättningar",
    text: `Fennys prisspann bygger på egna offerter och genomförda produktioner (${PRICE_DATA_BASIS.sampleSize} uppdrag) och anges exkl. moms. ${NATIONAL_PRICING_NOTE} Aktuella spann per tjänst står på prissidan.`,
    source: "Prisunderlaget",
    sourcePath: "/priser",
  },
  {
    id: "leverans",
    label: "Leverans",
    text: `Första klippet i ett Pulse-abonnemang levereras inom ${DELIVERY_PROMISE} från strategimötet.`,
    source: "Leveranslöftet",
    sourcePath: "/pulse",
  },
  {
    id: "orter",
    label: "Orter",
    text: `Fenny har kontor i ${list(F.officeCities)} och crew på plats i ${list(F.localTeamCities)}. ${F.serviceAreaSummary}`,
    source: "Ortsdata",
    sourcePath: "/kontakt",
  },
  {
    id: "bolag",
    label: "Bolag och organisationsnummer",
    text: `Fenny är ett varumärke som drivs av ${F.legalName}, organisationsnummer ${F.orgNumber}, momsregistreringsnummer ${F.vatId}.`,
    source: "Bolagsverket",
  },
  {
    id: "grundat",
    label: "Grundat",
    text: `Varumärket Fenny har varit verksamt sedan ${F.foundedYear}.`,
    source: "Fennys företagsfakta",
  },
  {
    id: "kunder",
    label: "Kunder",
    text: `Fenny har producerat film för bland andra ${list(F.clients)}. Namnen publiceras med kundernas godkännande.`,
    source: "Godkänd kundlista",
    sourcePath: "/case",
  },
  {
    id: "kontakt",
    label: "Kontakt",
    text: `Fenny nås på ${F.email} och ${F.phoneDisplay}, ${F.openingHours.toLowerCase()}. Besöksadress: ${F.postalAddress.streetAddress}, ${F.postalAddress.addressLocality}.`,
    source: "Företagsuppgifter",
    sourcePath: "/kontakt",
  },
];

export const Route = createFileRoute("/fenny-i-korthet")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(aeoGeo({ path: "/fenny-i-korthet", name: TITLE, description: DESC })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Om Fenny", path: "/om-fenny" },
            { name: "Fenny i korthet", path: "/fenny-i-korthet" },
          ]),
        ),
      },
    ],
  }),
  component: FactPage,
});

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });

function FactPage() {
  return (
    <div className="container-wide pt-32 pb-24 md:pt-40">
      <div className="mb-4 text-xs uppercase tracking-[0.22em] text-butter">Fakta</div>
      <h1 className="max-w-[22ch] text-4xl font-semibold leading-tight text-foreground md:text-6xl">
        Fenny i korthet
      </h1>
      <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
        Varje stycke nedan står för sig själv och kan citeras direkt. Länka till ett
        enskilt faktum med dess ankare, till exempel{" "}
        <code className="text-foreground">fenny.se/fenny-i-korthet#leverans</code>.
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Senast granskad {fmt(REVIEWED)} av {OWNER} ·{" "}
        <Link to="/redaktionell-policy" className="underline underline-offset-4 hover:text-butter">
          Redaktionell policy
        </Link>
      </p>

      <dl className="mt-14 grid gap-6 md:grid-cols-2">
        {FACTS.map((f) => (
          <div key={f.id} id={f.id} className="scroll-mt-28 rounded-2xl border border-border bg-card p-6">
            <dt className="flex items-baseline justify-between gap-4">
              <span className="text-sm font-semibold uppercase tracking-wider text-butter">{f.label}</span>
              <a href={`#${f.id}`} className="text-xs text-muted-foreground hover:text-foreground" aria-label={`Länk till ${f.label}`}>
                #{f.id}
              </a>
            </dt>
            <dd className="mt-3 text-pretty text-base text-foreground">{f.text}</dd>
            <dd className="mt-3 text-xs text-muted-foreground">
              Källa:{" "}
              {f.sourcePath ? (
                <a href={f.sourcePath} className="underline underline-offset-4 hover:text-butter">{f.source}</a>
              ) : (
                f.source
              )}{" "}
              · Granskad {REVIEWED}
            </dd>
          </div>
        ))}
      </dl>

      <section id="pastaenden" className="mt-20 scroll-mt-28">
        <h2 className="text-2xl font-semibold text-foreground md:text-3xl">Påståendelista</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Alla påståenden om pris, leverans och kunder som vi publicerar, med källa,
          granskningsdatum och ansvarig.
        </p>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-border text-muted-foreground">
              <tr>
                <th className="py-3 pr-4 font-medium">Påstående</th>
                <th className="py-3 pr-4 font-medium">Källa</th>
                <th className="py-3 pr-4 font-medium">Granskad</th>
                <th className="py-3 font-medium">Ansvarig</th>
              </tr>
            </thead>
            <tbody>
              {FACTS.filter((f) => ["pulse", "pris", "leverans", "kunder", "orter", "bolag"].includes(f.id)).map((f) => (
                <tr key={f.id} className="border-b border-border/60 align-top">
                  <td className="py-3 pr-4 text-foreground">
                    <a href={`#${f.id}`} className="hover:text-butter">{f.label}</a>
                  </td>
                  <td className="py-3 pr-4 text-muted-foreground">{f.source}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{REVIEWED}</td>
                  <td className="py-3 text-muted-foreground">{OWNER}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
